// The business's widget configuration (WidgetConfigV1), as the widget reads
// it. The source of truth is the Zod schema in the Aevo repository,
// `packages/contracts/src/widget/config.ts`; Echo serves its parsed output at
// `GET /widget/v1/config?widget=wgt_…`. The widget does not ship Zod, so this
// file mirrors the output shape by hand and `normalizeWidgetConfig` applies
// the same defaults and limits, field by field: a value the widget cannot
// use falls back to the default instead of breaking the page.
//
// With no widget config at all (no `widgetId`, or Echo answers `config:
// null`) the widget keeps today's copy and look: `legacyWidgetConfig()`.

export const WIDGET_POSITIONS = [
  'top-left',
  'top-center',
  'top-right',
  'center-left',
  'center-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
] as const
export type WidgetPosition = (typeof WIDGET_POSITIONS)[number]

export const WIDGET_RESIZE_CORNERS = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
  'none',
] as const
export type WidgetResizeCorner = (typeof WIDGET_RESIZE_CORNERS)[number]

export const WIDGET_ATTACHMENT_EXTENSIONS = [
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'webp',
  'gif',
  'heic',
  'svg',
  'eps',
  'ai',
  'psd',
  'indd',
  'indt',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'csv',
  'txt',
  'zip',
] as const
export type WidgetAttachmentExtension = (typeof WIDGET_ATTACHMENT_EXTENSIONS)[number]

export const WIDGET_PRE_CHAT_FIELDS = ['name', 'email', 'phone', 'topic'] as const
export type WidgetPreChatField = (typeof WIDGET_PRE_CHAT_FIELDS)[number]

export const WIDGET_MEMORY_MINUTES = [0, 60, 1440, 10080] as const
export const WIDGET_LAUNCHER_ICONS = [
  'message',
  'message-dots',
  'messages',
  'help',
  'headset',
  'avatar',
] as const
export type WidgetLauncherIcon = (typeof WIDGET_LAUNCHER_ICONS)[number]
export const WIDGET_FONTS = ['hanken', 'inter', 'system', 'serif'] as const
export type WidgetFont = (typeof WIDGET_FONTS)[number]
export type AnnouncementTone = 'info' | 'warning' | 'critical'
export type WidgetTheme = 'light' | 'dark' | 'auto'

export interface WidgetConfig {
  version: 1
  content: {
    identity: {
      assistantName: string
      subtitle: string
      teamName: string
      avatarUrl: string | null
      logoUrl: string | null
    }
    welcome: {
      title: string
      message: string
      announcement: { text: string; tone: AnnouncementTone }
      suggestedPrompts: string[]
    }
    composer: {
      placeholder: string
      attachments: { enabled: boolean; extensions: WidgetAttachmentExtension[] }
    }
    away: { followBusinessHours: boolean; message: string; askForEmail: boolean }
    preChat: {
      enabled: boolean
      fields: { key: WidgetPreChatField; required: boolean }[]
      privacyNote: string
      privacyUrl: string | null
    }
    conversation: {
      transcriptDownload: boolean
      transcriptTitle: string
      memoryMinutes: number
      followUpSuggestions: boolean
      showSources: boolean
      confirmActions: boolean
      showReasoningSummary: boolean
      replyFeedback: boolean
    }
  }
  appearance: {
    brandColor: string
    theme: WidgetTheme
    corners: 'sharp' | 'soft' | 'round'
    font: WidgetFont
    density: 'comfortable' | 'compact'
    launcher: {
      icon: WidgetLauncherIcon
      shape: 'round' | 'pill'
      label: string
      size: 'sm' | 'md' | 'lg'
    }
    placement: {
      position: WidgetPosition
      offsetX: number
      offsetY: number
      panelWidth: number
      panelHeight: number
      draggable: boolean
      resizeCorner: WidgetResizeCorner
    }
    showBranding: boolean
  }
}

/** Any subset of a config, as `update()` and the preview protocol accept it. */
export type WidgetConfigPatch = DeepPartial<WidgetConfig>
export type DeepPartial<T> = T extends readonly unknown[]
  ? T
  : T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T

/** The contract's defaults: what `{}` parses to. */
export function defaultWidgetConfig(): WidgetConfig {
  return {
    version: 1,
    content: {
      identity: {
        assistantName: 'Assistant',
        subtitle: 'AI assistant · humans on hand',
        teamName: 'Our team',
        avatarUrl: null,
        logoUrl: null,
      },
      welcome: {
        title: 'How can we help?',
        message: '',
        announcement: { text: '', tone: 'info' },
        suggestedPrompts: [
          'How do I track my order?',
          'I need help with a return',
          'Speak to someone',
        ],
      },
      composer: {
        placeholder: 'Write a message…',
        attachments: {
          enabled: true,
          extensions: ['pdf', 'png', 'jpg', 'jpeg', 'eps', 'ai', 'psd', 'indd', 'indt'],
        },
      },
      away: {
        followBusinessHours: true,
        message: "We're away right now. Leave a message and we'll reply by email.",
        askForEmail: true,
      },
      preChat: {
        enabled: false,
        fields: [
          { key: 'name', required: false },
          { key: 'email', required: true },
        ],
        privacyNote: '',
        privacyUrl: null,
      },
      conversation: {
        transcriptDownload: true,
        transcriptTitle: 'Conversation transcript',
        memoryMinutes: 1440,
        followUpSuggestions: true,
        showSources: true,
        confirmActions: true,
        showReasoningSummary: false,
        replyFeedback: true,
      },
    },
    appearance: {
      brandColor: '#4d99d5',
      theme: 'light',
      corners: 'soft',
      font: 'hanken',
      density: 'comfortable',
      launcher: { icon: 'message', shape: 'round', label: 'Chat with us', size: 'md' },
      placement: {
        position: 'bottom-right',
        offsetX: 20,
        offsetY: 20,
        panelWidth: 400,
        panelHeight: 720,
        draggable: false,
        resizeCorner: 'top-left',
      },
      showBranding: true,
    },
  }
}

/**
 * Today's widget, for embeds without a widget config: the copy, colours and
 * sizes the widget had before the studio existed. Branding stays off, as it
 * was never shown.
 */
export function legacyWidgetConfig(): WidgetConfig {
  const config = defaultWidgetConfig()
  config.content.identity.assistantName = 'Jon'
  config.content.identity.teamName = 'Solopress team'
  config.content.welcome.title = 'No messages yet'
  config.content.welcome.message =
    'Start the conversation with an agent by typing a message or choosing a topic below.'
  config.content.welcome.announcement.tone = 'critical'
  config.content.welcome.suggestedPrompts = [
    'How do I track my order?',
    'I need help with a return',
    'Speak to an agent',
    'Pricing inquiry',
  ]
  config.content.composer.placeholder = 'Send us your message to get started...'
  config.content.conversation.transcriptTitle = 'Solopress conversation transcript'
  config.content.conversation.showSources = false
  config.content.conversation.confirmActions = false
  config.content.conversation.replyFeedback = false
  config.content.away.followBusinessHours = false
  config.appearance.placement.panelHeight = 800
  config.appearance.showBranding = false
  return config
}

// ---------------------------------------------------------------------------
// Normalisation

type Obj = Record<string, unknown>

function isObj(value: unknown): value is Obj {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function str(raw: unknown, fallback: string, max: number, opts: { min?: number } = {}): string {
  if (typeof raw !== 'string') return fallback
  const value = raw.trim()
  if (value.length > max) return value.slice(0, max)
  if (opts.min && value.length < opts.min) return fallback
  return value
}

function bool(raw: unknown, fallback: boolean): boolean {
  return typeof raw === 'boolean' ? raw : fallback
}

function oneOf<T extends string | number>(raw: unknown, allowed: readonly T[], fallback: T): T {
  return (allowed as readonly unknown[]).includes(raw) ? (raw as T) : fallback
}

function int(raw: unknown, fallback: number, min: number, max: number): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return fallback
  return Math.min(max, Math.max(min, Math.round(raw)))
}

/** https URLs (and http://localhost for testing), as the contract allows. */
export function assetUrl(raw: unknown, fallback: string | null): string | null {
  if (raw === null) return null
  if (typeof raw !== 'string' || !raw.trim()) return fallback
  try {
    const url = new URL(raw.trim())
    if (url.protocol === 'https:') return url.href
    if (url.protocol === 'http:' && url.hostname === 'localhost') return url.href
  } catch {
    /* not a URL */
  }
  return fallback
}

const HEX = /^#[0-9a-fA-F]{6}$/

/**
 * Fill `raw` (a served config, a stored one, or a patch) over `base`. Unknown
 * keys are dropped; each invalid value keeps the base value.
 */
export function normalizeWidgetConfig(raw: unknown, base: WidgetConfig = defaultWidgetConfig()): WidgetConfig {
  const r = isObj(raw) ? raw : {}
  const content = isObj(r.content) ? r.content : {}
  const appearance = isObj(r.appearance) ? r.appearance : {}

  const identityRaw = isObj(content.identity) ? content.identity : {}
  const welcomeRaw = isObj(content.welcome) ? content.welcome : {}
  const announcementRaw = isObj(welcomeRaw.announcement) ? welcomeRaw.announcement : {}
  const composerRaw = isObj(content.composer) ? content.composer : {}
  const attachmentsRaw = isObj(composerRaw.attachments) ? composerRaw.attachments : {}
  const awayRaw = isObj(content.away) ? content.away : {}
  const preChatRaw = isObj(content.preChat) ? content.preChat : {}
  const conversationRaw = isObj(content.conversation) ? content.conversation : {}
  const launcherRaw = isObj(appearance.launcher) ? appearance.launcher : {}
  const placementRaw = isObj(appearance.placement) ? appearance.placement : {}

  const b = base.content
  const ba = base.appearance

  const prompts = Array.isArray(welcomeRaw.suggestedPrompts)
    ? welcomeRaw.suggestedPrompts
        .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
        .map((p) => p.trim().slice(0, 80))
        .slice(0, 8)
    : b.welcome.suggestedPrompts

  const extensions = Array.isArray(attachmentsRaw.extensions)
    ? Array.from(
        new Set(
          attachmentsRaw.extensions.filter((e): e is WidgetAttachmentExtension =>
            (WIDGET_ATTACHMENT_EXTENSIONS as readonly unknown[]).includes(e),
          ),
        ),
      )
    : b.composer.attachments.extensions

  const fields = Array.isArray(preChatRaw.fields)
    ? preChatRaw.fields
        .filter(isObj)
        .filter((f) => (WIDGET_PRE_CHAT_FIELDS as readonly unknown[]).includes(f.key))
        .map((f) => ({ key: f.key as WidgetPreChatField, required: f.required === true }))
        .filter((f, i, all) => all.findIndex((g) => g.key === f.key) === i)
    : b.preChat.fields

  const brandColor =
    typeof appearance.brandColor === 'string' && HEX.test(appearance.brandColor.trim())
      ? appearance.brandColor.trim().toLowerCase()
      : ba.brandColor

  return {
    version: 1,
    content: {
      identity: {
        assistantName: str(identityRaw.assistantName, b.identity.assistantName, 40, { min: 1 }),
        subtitle: str(identityRaw.subtitle, b.identity.subtitle, 80),
        teamName: str(identityRaw.teamName, b.identity.teamName, 40, { min: 1 }),
        avatarUrl: assetUrl(identityRaw.avatarUrl, b.identity.avatarUrl),
        logoUrl: assetUrl(identityRaw.logoUrl, b.identity.logoUrl),
      },
      welcome: {
        title: str(welcomeRaw.title, b.welcome.title, 60),
        message: str(welcomeRaw.message, b.welcome.message, 240),
        announcement: {
          text: str(announcementRaw.text, b.welcome.announcement.text, 160),
          tone: oneOf(announcementRaw.tone, ['info', 'warning', 'critical'] as const, b.welcome.announcement.tone),
        },
        suggestedPrompts: prompts,
      },
      composer: {
        placeholder: str(composerRaw.placeholder, b.composer.placeholder, 80),
        attachments: {
          enabled: bool(attachmentsRaw.enabled, b.composer.attachments.enabled),
          extensions,
        },
      },
      away: {
        followBusinessHours: bool(awayRaw.followBusinessHours, b.away.followBusinessHours),
        message: str(awayRaw.message, b.away.message, 240),
        askForEmail: bool(awayRaw.askForEmail, b.away.askForEmail),
      },
      preChat: {
        enabled: bool(preChatRaw.enabled, b.preChat.enabled),
        fields,
        privacyNote: str(preChatRaw.privacyNote, b.preChat.privacyNote, 200),
        privacyUrl: assetUrl(preChatRaw.privacyUrl, b.preChat.privacyUrl),
      },
      conversation: {
        transcriptDownload: bool(conversationRaw.transcriptDownload, b.conversation.transcriptDownload),
        transcriptTitle: str(conversationRaw.transcriptTitle, b.conversation.transcriptTitle, 60),
        memoryMinutes: oneOf(conversationRaw.memoryMinutes, WIDGET_MEMORY_MINUTES, b.conversation.memoryMinutes as 1440),
        followUpSuggestions: bool(conversationRaw.followUpSuggestions, b.conversation.followUpSuggestions),
        showSources: bool(conversationRaw.showSources, b.conversation.showSources),
        confirmActions: bool(conversationRaw.confirmActions, b.conversation.confirmActions),
        showReasoningSummary: bool(conversationRaw.showReasoningSummary, b.conversation.showReasoningSummary),
        replyFeedback: bool(conversationRaw.replyFeedback, b.conversation.replyFeedback),
      },
    },
    appearance: {
      brandColor,
      theme: oneOf(appearance.theme, ['light', 'dark', 'auto'] as const, ba.theme),
      corners: oneOf(appearance.corners, ['sharp', 'soft', 'round'] as const, ba.corners),
      font: oneOf(appearance.font, WIDGET_FONTS, ba.font),
      density: oneOf(appearance.density, ['comfortable', 'compact'] as const, ba.density),
      launcher: {
        icon: oneOf(launcherRaw.icon, WIDGET_LAUNCHER_ICONS, ba.launcher.icon),
        shape: oneOf(launcherRaw.shape, ['round', 'pill'] as const, ba.launcher.shape),
        label: str(launcherRaw.label, ba.launcher.label, 24),
        size: oneOf(launcherRaw.size, ['sm', 'md', 'lg'] as const, ba.launcher.size),
      },
      placement: {
        position: oneOf(placementRaw.position, WIDGET_POSITIONS, ba.placement.position),
        offsetX: int(placementRaw.offsetX, ba.placement.offsetX, 0, 120),
        offsetY: int(placementRaw.offsetY, ba.placement.offsetY, 0, 120),
        panelWidth: int(placementRaw.panelWidth, ba.placement.panelWidth, 320, 480),
        panelHeight: int(placementRaw.panelHeight, ba.placement.panelHeight, 480, 800),
        draggable: bool(placementRaw.draggable, ba.placement.draggable),
        resizeCorner: oneOf(placementRaw.resizeCorner, WIDGET_RESIZE_CORNERS, ba.placement.resizeCorner),
      },
      showBranding: bool(appearance.showBranding, ba.showBranding),
    },
  }
}

/** `normalizeWidgetConfig(patch, current)`: apply any subset over the current config. */
export function applyWidgetConfigPatch(current: WidgetConfig, patch: unknown): WidgetConfig {
  return normalizeWidgetConfig(patch, current)
}

/** The `accept` attribute for the file picker: `.pdf,.png,…`. */
export function acceptAttribute(extensions: readonly string[]): string {
  return extensions.map((e) => `.${e}`).join(',')
}
